import { IFormField } from "c8-platform";
import clsx from "clsx";
import { FlexUniversalDrawer } from "csi/components/index";
import {
	DndMode,
	DndStore,
	DndStoreContext,
	DragAndColumnDropWrapper,
	DragPreviewLayer
} from "designSystem/dnd/index";
import { useSyncLocalStore } from "designSystem/hooks";
import {
	FlexColumn,
	SearchTextField
} from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { EditFormDefinitionAttributesList, FORM_DEFINITION_ATTRIBUTE_DRAG_TYPE } from "./EditFormDefinitionAttributesList";
import { FormAttributesPanel } from "./FormAttributesPanel";
import { FormDefinitionModeHeader } from "./FormDefinitionModeHeader";
import { useFormDefinitionStore } from "./FormDefinitionStore";
import { SelectableFormField } from "./SelectableFormField";
import { renderFieldOverlay } from "./SelectableFormFieldOverlay";
import css from "./SelectableFormFields.module.css";

const FORM_FIELD_DRAG_TYPE = "FormField";
const FIELD_FLEX_DRAWER_WIDTH = 340;
const MAX_FORM_FIELD_WIDTH = 680;
const FORM_FIELD_CENTERING_MARGIN = "0 auto";
const Z_INDEX_DRAG_PREVIEW = 5;

export const SelectableFormFields: FunctionComponent = observer(function SelectableFormFields() {
	const {
		activeField,
		closeDrawerAndRemoveActiveField,
		generateFormFieldKey,
		isActiveFieldDrawerOpen,
		isEditFormDefinitionMode,
		onSearchChange,
		orderFields,
		orderedFormFields,
	} = useFormDefinitionStore();
	const dndStore = useSyncLocalStore(() => new DndStore<IFormField>());
	const renderItem = (field: IFormField) => (<SelectableFormField field={field} key={field.id} />);
	const loc = useLocale();
	const searchForAttributesText = loc.resource("FormBuilderAttributeSearch");

	const drawers = {
		left: {
			open: isEditFormDefinitionMode,
			drawerNode: (
				<FlexColumn className={clsx(css.drawerNodeContainer, css.drawerNodeContainerLeft)} p={4}>
					<SearchTextField onChange={onSearchChange} placeholder={searchForAttributesText} />
					<EditFormDefinitionAttributesList />
				</FlexColumn>
			),
			hideWhenClosed: true,
			sizeOpen: FIELD_FLEX_DRAWER_WIDTH,
			disabled: true,
		},
		right: {
			open: activeField && isActiveFieldDrawerOpen,
			drawerNode: (
				activeField && <FormAttributesPanel />
			),
			hideWhenClosed: true,
			onToggleOpen: (open) => {
				if (!open) {
					closeDrawerAndRemoveActiveField();
				}
			},
			sizeOpen: FIELD_FLEX_DRAWER_WIDTH,
			disabled: true,
		},
	};

	const commonFlexColumnProps = {
		gap: 4,
		p: 4,
		sx: {
			margin: FORM_FIELD_CENTERING_MARGIN,
			maxWidth: MAX_FORM_FIELD_WIDTH,
		},
	};

	const clickAwayLayer = <div className={css.clickAwayLayer} onClick={closeDrawerAndRemoveActiveField} />;

	return (
		<DndStoreContext.Provider value={dndStore}>
			<FlexColumn className={css.selectableFormFieldsContainer}>
				<FormDefinitionModeHeader />
				<FlexUniversalDrawer
					className={css.flexUniversalDrawerRootContainer}
					drawers={drawers}
					mainContentClassName={css.flexUniversalDrawerMainContentContainer}
					middleContainerClassName={css.flexUniversalDrawerMiddleContainer}
				>
					{isEditFormDefinitionMode ? (
						<DragAndColumnDropWrapper<IFormField>
							canDrag
							clickAwayLayer={clickAwayLayer}
							dragPreviewLayer={<DragPreviewLayer previewComponent={renderFieldOverlay} zIndex={Z_INDEX_DRAG_PREVIEW} />}
							dragType={FORM_FIELD_DRAG_TYPE}
							dropTypes={[FORM_DEFINITION_ATTRIBUTE_DRAG_TYPE, FORM_FIELD_DRAG_TYPE]}
							generateKey={generateFormFieldKey}
							isFullScreen
							isRootDropWrapper
							list={orderedFormFields}
							mode={DndMode.Divider}
							onDrop={orderFields}
							renderItem={renderItem}
							useExternalContainerPosition
							{...commonFlexColumnProps}
						/>
						
					) : (
						<FlexColumn {...commonFlexColumnProps}>
							{orderedFormFields.map((field) => renderItem(field))}
							{clickAwayLayer}
						</FlexColumn>
					)}
				</FlexUniversalDrawer>
			</FlexColumn>
		</DndStoreContext.Provider>
	);
});
